import React, { useMemo, useState } from 'react';
import { cn } from '../cn';
import { DataTableShell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../data-table';
import { InfoTooltip } from './InfoTooltip';
import { Sparkline } from './Sparkline';
import { formatDeltaPct, deltaTone } from './format';
/**
 * Sub-unit drill-down table (SA→agents, agent→shops, shop→top players):
 * column tooltips, right-aligned numerics, optional row navigation.
 * Doubles as the "table view" relief required by the chart palette validation.
 */
export function SubUnitStatsTable({ columns, rows, rowKey, loading, emptyLabel = 'Nothing to show for this period', onRowClick, className, }) {
    const [sort, setSort] = useState(null);
    const sortedRows = useMemo(() => {
        if (!sort)
            return rows;
        const col = columns.find((c) => c.key === sort.key && c.sortable && c.sortValue);
        if (!(col === null || col === void 0 ? void 0 : col.sortValue))
            return rows;
        const dir = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = Number(col.sortValue(a)) || 0;
            const bv = Number(col.sortValue(b)) || 0;
            if (av === bv)
                return 0;
            return av < bv ? -dir : dir;
        });
    }, [columns, rows, sort]);
    const toggleSort = (key) => {
        setSort((prev) => {
            if (!prev || prev.key !== key)
                return { key, dir: 'desc' };
            return { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' };
        });
    };
    return (React.createElement(DataTableShell, { className: className },
        React.createElement(Table, null,
            React.createElement(TableHeader, null,
                React.createElement(TableRow, null, columns.map((col) => (React.createElement(TableHead, { key: col.key, className: cn(col.align === 'right' && 'text-right') }, col.sortable && col.sortValue ? (React.createElement("button", { type: "button", className: cn('inline-flex items-center gap-1 font-medium text-gray-700 hover:text-gray-900', col.align === 'right' && 'w-full justify-end'), onClick: () => toggleSort(col.key), "aria-label": `Sort by ${col.header}` },
                    React.createElement("span", { className: "inline-flex items-center gap-1" },
                        col.header,
                        col.tooltip ? (React.createElement(InfoTooltip, { title: col.header, content: col.tooltip, align: "left" })) : null),
                    React.createElement("span", { className: "text-[10px] text-gray-400", "aria-hidden": true }, (sort === null || sort === void 0 ? void 0 : sort.key) === col.key ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'))) : (React.createElement("span", { className: "inline-flex items-center gap-1" },
                    col.header,
                    col.tooltip ? React.createElement(InfoTooltip, { title: col.header, content: col.tooltip, align: "left" }) : null))))))),
            React.createElement(TableBody, null, loading ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: columns.length, className: "py-8 text-center text-sm text-gray-400" }, "Loading\u2026"))) : sortedRows.length === 0 ? (React.createElement(TableRow, null,
                React.createElement(TableCell, { colSpan: columns.length, className: "py-8 text-center text-sm text-gray-400" }, emptyLabel))) : (sortedRows.map((row) => (React.createElement(TableRow, { key: rowKey(row), className: cn(onRowClick && 'cursor-pointer hover:bg-orange-50/40'), onClick: onRowClick ? () => onRowClick(row) : undefined }, columns.map((col) => (React.createElement(TableCell, { key: col.key, className: cn('text-sm', col.align === 'right' && 'text-right tabular-nums') }, col.render(row))))))))))));
}
/** Delta cell for sub-unit tables: value + colored vs-previous pill. */
export const DeltaCell = ({ kpi, format, goodWhen = 'up' }) => {
    if (!kpi)
        return React.createElement("span", { className: "text-gray-300" }, "\u2014");
    const tone = deltaTone(kpi.deltaPct, goodWhen);
    const toneClass = tone === 'positive' ? 'text-emerald-700' : tone === 'negative' ? 'text-red-700' : 'text-gray-500';
    return (React.createElement("span", { className: "inline-flex items-baseline gap-1.5" },
        React.createElement("span", { className: "font-medium text-gray-900 tabular-nums" }, format(kpi.value)),
        React.createElement("span", { className: cn('text-xs tabular-nums', toneClass) }, formatDeltaPct(kpi.deltaPct))));
};
/** Sparkline cell wrapper (fixed size for table alignment). */
export const TrendCell = ({ values, label }) => (React.createElement(Sparkline, { values: values, label: label }));
export default SubUnitStatsTable;
